
import { CSSProp } from "../../fable_modules/Fable.React.9.4.0/Fable.React.Props.fs.js";
import { Union } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { nonBinaryTransitionsBigs, nonBinaryTransitionsWords, binaryTransitions, NonBinaryTransition, NonBinaryTransition_$reflection, BinaryTransition_$reflection } from "./WaveSlice.js";
import { union_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { valueColumnFontFamily, valueOnWaveEdgePadding, valueOnWavePadding, waveLegendMaxChars, viewBoxHeight, yBot, yTop, nonBinaryTransLen } from "./WaveSimTypes.js";
import { XYPos } from "../../Common/CommonTypes.js";
import { getTextWidthInPixels, Text$, defaultText } from "../../Common/DrawHelpers.js";
import { unzip, mapIndexed, concat as concat_1, append, pairwise, item, indexed, map } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { uncurry2, safeHash, equals } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { waveRowProps, wavePolylineStyle, wavePolyfillStyle, singleWaveWidth, singleValueOnWaveProps } from "./WaveSimStyle.js";
import * as react from "react";
import { keyValueList } from "../../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { append as append_1, ofArray, singleton, concat } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { BigIntToPaddedString, UInt32ToPaddedString } from "../../Simulator/NumberHelpers.js";
import { Array_distinct } from "../../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { finaliseStore, initGapStore, addGapToStore } from "./EvilHoverCache.js";
import { WaveSimModel, Gap } from "../../Model/ModelType.js";
import { heldWindow, slice as slice_4 } from "./WaveData.js";
import { printf, toFail } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { valueAtSample, tryDrawn, put, specOf, Drawn } from "./WaveDrawn.js";
import { toArray, orElse } from "../../fable_modules/fable-library-js.5.13.0/Option.js";
import { forAll } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";

export const Constants_generateVisibleOnly = true;

export const Constants_showPerfLogs = false;

export const Constants_inlineNoWrap = new CSSProp(/* WhiteSpace */ 392, ["nowrap"]);

/**
 * create a new array which samples the old one every mult cycles.
 * start: index of first cycle.
 * count: number of samples.
 * Determines whether a clock cycle is generated with a vertical bar at the beginning,
 * denoting that a waveform changes value at the start of that clock cycle. NB this
 * does not determine whether a waveform changes value at the end of that clock cycle.
 * TODO: Remove this since it is unnecessary. Can use WaveValues instead.
 * Determines whether a non-binary waveform changes value at the beginning of that clock cycle.
 * Waveforms can be either binary or non-binary; these have different properties.
 */
export class Transition extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["BinaryTransition", "NonBinaryTransition"];
    }
}

export function Transition_$reflection() {
    return union_type("WaveSimSVGs.Transition", [], Transition, () => [[["Item", BinaryTransition_$reflection()]], [["Item", NonBinaryTransition_$reflection()]]]);
}

/**
 * If true, then show cross-hatch only for non-binary waves when wave is changing value very fast.
 */
export function highZoom(clkCycleWidth) {
    return clkCycleWidth < (2 * nonBinaryTransLen);
}

/**
 * Left-shift non-binary waveforms by this much.
 */
export function xShift(clkCycleWidth) {
    if (highZoom(clkCycleWidth)) {
        return clkCycleWidth / 2;
    }
    else {
        return nonBinaryTransLen;
    }
}

/**
 * Retrieve value of wave at given clock cycle as an int.
 * At extra (sampling) zoom this allows detail clock cycles within one sample
 * therefore clkCycleDetail IS NOT scaled the same as the sample numbers used
 * everywhere else.
 * Make left and right x-coordinates for a clock cycle.
 */
export function makeXCoords(clkCycleWidth, clkCycle, transition) {
    if (transition.tag === 1) {
        return [(clkCycle * clkCycleWidth) - xShift(clkCycleWidth), ((clkCycle + 1) * clkCycleWidth) - xShift(clkCycleWidth)];
    }
    else {
        return [clkCycle * clkCycleWidth, (clkCycle + 1) * clkCycleWidth];
    }
}

/**
 * Make top-left, top-right, bottom-left, bottom-right coordinates for a clock cycle.
 */
export function makeCoords(clkCycleWidth, clkCycle, transition) {
    const patternInput = makeXCoords(clkCycleWidth, clkCycle, transition);
    const xRight = patternInput[1];
    const xLeft = patternInput[0];
    return [new XYPos(xLeft, yTop), new XYPos(xRight, yTop), new XYPos(xLeft, yBot), new XYPos(xRight, yBot)];
}

/**
 * Generate points for a binary waveform
 */
export function binaryWavePoints(clkCycleWidth, startCycle, index, transition) {
    const patternInput = makeCoords(clkCycleWidth, index, new Transition(/* BinaryTransition */ 0, [transition]));
    switch (transition.tag) {
        case 1:
        case 3:
            return [patternInput[0], patternInput[1]];
        default:
            return [patternInput[2], patternInput[3]];
    }
}

/**
 * Generate polyline points for a non-binary waveform via transition info.
 */
export function nonBinaryWavePoints(clkCycleWidth, startCycle, index, transition) {
    const xLeft = makeXCoords(clkCycleWidth, index, new Transition(/* NonBinaryTransition */ 1, [transition]))[0];
    const patternInput_1 = makeCoords(clkCycleWidth, index, new Transition(/* NonBinaryTransition */ 1, [transition]));
    const topR = patternInput_1[1];
    const botR = patternInput_1[3];
    const matchValue = new XYPos(xLeft + xShift(clkCycleWidth), 0.5 * viewBoxHeight);
    const crossHatchTop = new XYPos(xLeft + (2 * xShift(clkCycleWidth)), yTop);
    const crossHatchMid = matchValue;
    const crossHatchBot = new XYPos(xLeft + (2 * xShift(clkCycleWidth)), yBot);
    if (transition.tag === 1) {
        return [[topR], [botR]];
    }
    else if (highZoom(clkCycleWidth)) {
        return [[crossHatchMid, crossHatchTop], [crossHatchMid, crossHatchBot]];
    }
    else {
        return [[crossHatchMid, crossHatchTop, topR], [crossHatchMid, crossHatchBot, botR]];
    }
}

/**
 * Generate polyfill points for a non-binary gap via gap info.
 */
export function nonBinaryFillPoints(startCycle, clkCycleWidth, gap) {
    const start = (gap.Start - startCycle) | 0;
    const xLeft = makeXCoords(clkCycleWidth, start, new Transition(/* NonBinaryTransition */ 1, [NonBinaryTransition.Change]))[0];
    const xRight = makeXCoords(clkCycleWidth, (start + gap.Length) - 1, new Transition(/* NonBinaryTransition */ 1, [NonBinaryTransition.Change]))[1];
    const matchValue = new XYPos(xLeft + xShift(clkCycleWidth), 0.5 * viewBoxHeight);
    const matchValue_1 = new XYPos(xLeft + (2 * xShift(clkCycleWidth)), yTop);
    const matchValue_2 = new XYPos(xLeft + (2 * xShift(clkCycleWidth)), yBot);
    const crossHatchMidL = matchValue;
    return [crossHatchMidL, matchValue_1, new XYPos(xRight, yTop), new XYPos(xRight + xShift(clkCycleWidth), 0.5 * viewBoxHeight), new XYPos(xRight, yBot), matchValue_2, crossHatchMidL];
}

/**
 * Generates SVG to display non-binary values on waveforms.
 */
export function displayUInt32OnWave(wsModel, width, waveValues, transitions, gapCache) {
    let array_1;
    const textFont = wsModel.WSConfig.FontSize | 0;
    const textWeight = wsModel.WSConfig.FontWeight | 0;
    const textSpec = new Text$(defaultText.TextAnchor, `${textFont}px`, `${textWeight}`, "Helvetica", defaultText.Fill, defaultText.UserSelect, defaultText.DominantBaseline);
    const changeTransitions = map((tupledArg_1) => (tupledArg_1[0] | 0), (array_1 = indexed(transitions), array_1.filter((tupledArg) => equals(tupledArg[1], NonBinaryTransition.Change))), Int32Array);
    const makeTextElement = (isStart, start, waveValue) => {
        const props_2 = singleValueOnWaveProps(isStart, textFont, textWeight, start);
        return react.createElement("text", keyValueList(props_2, 1), waveValue);
    };
    return concat(map((gap) => {
        const gapCycle = (gap.Start - wsModel.StartCycle) | 0;
        const waveValue_1 = UInt32ToPaddedString(waveLegendMaxChars, wsModel.Radix, width, item(gapCycle, waveValues));
        const cycleWidth = 1 * singleWaveWidth(wsModel);
        const gapWidth = (gap.Length * cycleWidth) - (2 * nonBinaryTransLen);
        const singleWidth = 1 * getTextWidthInPixels(textSpec, waveValue_1);
        const doubleWidth = (2 * singleWidth) + valueOnWavePadding;
        if (gapWidth < (singleWidth * 1.05)) {
            let fill;
            const props = wavePolyfillStyle(Array_distinct(nonBinaryFillPoints(wsModel.StartCycle, cycleWidth, gap), {
                Equals: equals,
                GetHashCode: (x_1) => (safeHash(x_1) | 0),
            }));
            fill = react.createElement("polyline", keyValueList(props, 1));
            addGapToStore(gapCache, gap);
            return singleton(fill);
        }
        else if (gapWidth < (doubleWidth * 1.1)) {
            return singleton(makeTextElement(true, (gapCycle * cycleWidth) + (((gap.Length * cycleWidth) - singleWidth) / 2), waveValue_1));
        }
        else {
            ((gap.Length * cycleWidth) - valueOnWaveEdgePadding) - singleWidth;
            return ofArray([makeTextElement(true, (gapCycle * cycleWidth) + valueOnWaveEdgePadding, waveValue_1), makeTextElement(false, ((gapCycle + gap.Length) * cycleWidth) - valueOnWaveEdgePadding, waveValue_1)]);
        }
    }, map((tupledArg_2) => {
        const i1 = tupledArg_2[0] | 0;
        return new Gap(i1, tupledArg_2[1] - i1);
    }, pairwise(Constants_generateVisibleOnly ? map((loc) => ((loc + wsModel.StartCycle) | 0), (item(changeTransitions.length - 1, changeTransitions) !== wsModel.ShownCycles) ? append(changeTransitions, new Int32Array([wsModel.ShownCycles]), Int32Array) : append(changeTransitions, new Int32Array([wsModel.ShownCycles + 1]), Int32Array), Int32Array) : append(changeTransitions, new Int32Array([(wsModel.StartCycle + transitions.length) - 1]), Int32Array)))));
}

/**
 * Generates SVG to display <c>bigint</c> values on waveforms.
 */
export function displayBigIntOnWave(wsModel, width, waveValues, transitions, gapCache) {
    let array_1;
    const textFont = wsModel.WSConfig.FontSize | 0;
    const textWeight = wsModel.WSConfig.FontWeight | 0;
    const textSpec = new Text$(defaultText.TextAnchor, `${textFont}px`, `${textWeight}`, valueColumnFontFamily, defaultText.Fill, defaultText.UserSelect, defaultText.DominantBaseline);
    const changeTransitions = map((tupledArg_1) => (tupledArg_1[0] | 0), (array_1 = indexed(transitions), array_1.filter((tupledArg) => equals(tupledArg[1], NonBinaryTransition.Change))), Int32Array);
    const makeTextElement = (isStart, start, waveValue) => {
        const props_2 = singleValueOnWaveProps(isStart, textFont, textWeight, start);
        return react.createElement("text", keyValueList(props_2, 1), waveValue);
    };
    return concat(map((gap) => {
        let props;
        const gapCycle = (gap.Start - wsModel.StartCycle) | 0;
        const waveValue_1 = BigIntToPaddedString(waveLegendMaxChars, wsModel.Radix, width, item(gapCycle, waveValues));
        const cycleWidth = singleWaveWidth(wsModel);
        const gapWidth = (gap.Length * cycleWidth) - (2 * nonBinaryTransLen);
        const singleWidth = 1 * getTextWidthInPixels(textSpec, waveValue_1);
        const doubleWidth = (2 * singleWidth) + valueOnWavePadding;
        if (gapWidth < (singleWidth * 1.05)) {
            addGapToStore(gapCache, gap);
            return singleton((props = wavePolyfillStyle(Array_distinct(nonBinaryFillPoints(wsModel.StartCycle, cycleWidth, gap), {
                Equals: equals,
                GetHashCode: (x_1) => (safeHash(x_1) | 0),
            })), react.createElement("polyline", keyValueList(props, 1))));
        }
        else if (gapWidth < (doubleWidth * 3)) {
            return singleton(makeTextElement(true, (gapCycle * cycleWidth) + (((gap.Length * cycleWidth) - singleWidth) / 2), waveValue_1));
        }
        else {
            return ofArray([makeTextElement(true, (gapCycle * cycleWidth) + valueOnWaveEdgePadding, waveValue_1), makeTextElement(false, ((gapCycle + gap.Length) * cycleWidth) - valueOnWaveEdgePadding, waveValue_1)]);
        }
    }, map((tupledArg_2) => {
        const i1 = tupledArg_2[0] | 0;
        return new Gap(i1, tupledArg_2[1] - i1);
    }, pairwise(Constants_generateVisibleOnly ? map((loc) => ((loc + wsModel.StartCycle) | 0), (item(changeTransitions.length - 1, changeTransitions) !== wsModel.ShownCycles) ? append(changeTransitions, new Int32Array([wsModel.ShownCycles]), Int32Array) : append(changeTransitions, new Int32Array([wsModel.ShownCycles + 1]), Int32Array), Int32Array) : append(changeTransitions, new Int32Array([(wsModel.StartCycle + transitions.length) - 1]), Int32Array)))));
}

function makeWaveform(ws, wave, spec) {
    let props_4, children_4, props_2, props_6, children_6, clkCycleWidth, props_8, children_8;
    const makePolyline = (points) => {
        const props = wavePolylineStyle(Array_distinct(concat_1(points), {
            Equals: equals,
            GetHashCode: (x) => (safeHash(x) | 0),
        }));
        return react.createElement("polyline", keyValueList(props, 1));
    };
    const matchValue = slice_4(wave.DriverIndex, spec.Window);
    if (matchValue != null) {
        const sliceOfWave = matchValue;
        let patternInput_4;
        const matchValue_1 = wave.Width | 0;
        switch (matchValue_1) {
            case 0: {
                patternInput_4 = toFail(printf("Cannot have wave of width 0"));
                break;
            }
            case 1: {
                const transitions = binaryTransitions(sliceOfWave);
                let wavePoints;
                const waveWidth = singleWaveWidth(ws);
                wavePoints = Array_distinct(concat_1(mapIndexed((index, transition) => binaryWavePoints(waveWidth, ws.StartCycle, index, transition), transitions)), {
                    Equals: equals,
                    GetHashCode: (x_1) => (safeHash(x_1) | 0),
                });
                patternInput_4 = [(props_4 = waveRowProps(ws), (children_4 = [(props_2 = wavePolylineStyle(wavePoints), react.createElement("polyline", keyValueList(props_2, 1)))], react.createElement("svg", keyValueList(props_4, 1), ...children_4))), initGapStore(0)];
                break;
            }
            default:
                if (matchValue_1 <= 32) {
                    const patternInput = nonBinaryTransitionsWords(sliceOfWave);
                    const transitions_1 = patternInput[0];
                    let patternInput_1;
                    const waveWidth_1 = singleWaveWidth(ws);
                    patternInput_1 = unzip(mapIndexed((index_1, transition_1) => nonBinaryWavePoints(waveWidth_1, 0, index_1, transition_1), transitions_1));
                    const gapStore = initGapStore(~~(ws.ShownCycles / 2) + 1);
                    const valuesSVG = displayUInt32OnWave(ws, wave.Width, patternInput[1], transitions_1, gapStore);
                    finaliseStore(gapStore);
                    const polyLines = ofArray([makePolyline(patternInput_1[0]), makePolyline(patternInput_1[1])]);
                    patternInput_4 = [(props_6 = waveRowProps(ws), (children_6 = append_1(polyLines, valuesSVG), react.createElement("svg", keyValueList(props_6, 1), ...children_6))), gapStore];
                }
                else {
                    const patternInput_2 = nonBinaryTransitionsBigs(sliceOfWave);
                    const transitions_2 = patternInput_2[0];
                    const patternInput_3 = unzip(mapIndexed(uncurry2((clkCycleWidth = singleWaveWidth(ws), (index_2) => ((transition_2) => nonBinaryWavePoints(clkCycleWidth, 0, index_2, transition_2)))), transitions_2));
                    const gapStore_1 = initGapStore(~~(ws.ShownCycles / 2) + 1);
                    const valuesSVG_1 = displayBigIntOnWave(ws, wave.Width, patternInput_2[1], transitions_2, gapStore_1);
                    finaliseStore(gapStore_1);
                    patternInput_4 = [(props_8 = waveRowProps(ws), (children_8 = append_1(ofArray([makePolyline(patternInput_3[0]), makePolyline(patternInput_3[1])]), valuesSVG_1), react.createElement("svg", keyValueList(props_8, 1), ...children_8))), gapStore_1];
                }
        }
        return new Drawn(spec, patternInput_4[0], patternInput_4[1], sliceOfWave);
    }
    else {
        return undefined;
    }
}

/**
 * The waveform to put on screen for one wave: the view the controls ask for where its data is
 * here, and otherwise the one it is already showing.
 * 
 * Called from the view, for every wave, on every render. Three outcomes, in the order they are
 * tried: the waveform asked for has already been made, so it is reused; it has not, and the data
 * is here, so it is made and kept; the data is not here, so what is on screen stays there. Only
 * the last is a judgement, and it is the one the user asked for - a viewer that empties itself
 * while it waits looks broken, one that shows the last view for a moment does not.
 * 
 * None only before anything has ever been drawn for this wave, which is the moment a wave is added
 * to the selection. An empty row is right there: there is nothing older to show.
 */
export function drawnFor(ws, wave) {
    let held;
    const spec = specOf(ws, wave);
    const draw = (ws_1) => {
        let drawn;
        const option_1 = makeWaveform(ws_1, wave, specOf(ws_1, wave));
        if (option_1 != null) {
            return (drawn = option_1, (put(drawn), drawn));
        }
        else {
            return undefined;
        }
    };
    const matchValue = tryDrawn(wave.DriverIndex);
    let matchResult, drawn_2, onScreen;
    if (matchValue != null) {
        if (equals(matchValue.Spec, spec)) {
            matchResult = 0;
            drawn_2 = matchValue;
        }
        else {
            matchResult = 1;
            onScreen = matchValue;
        }
    }
    else {
        matchResult = 1;
        onScreen = matchValue;
    }
    switch (matchResult) {
        case 0:
            return drawn_2;
        default: {
            const matchValue_1 = draw(ws);
            if (matchValue_1 == null) {
                const matchValue_2 = heldWindow(wave.DriverIndex);
                let matchResult_1, held_1;
                if (matchValue_2 != null) {
                    if ((held = matchValue_2, forAll((d) => !equals(d.Spec.Window, held), toArray(onScreen)))) {
                        matchResult_1 = 0;
                        held_1 = matchValue_2;
                    }
                    else {
                        matchResult_1 = 1;
                    }
                }
                else {
                    matchResult_1 = 1;
                }
                switch (matchResult_1) {
                    case 0:
                        return orElse(draw(new WaveSimModel(ws.DefaultCursor, ws.WSConfig, ws.WSConfigDialog, ws.State, ws.WaveDetails, ws.SelectedWaves, ws.DefaultSelectionPending, ws.FetchFailedAtMs, held_1.StartSample, held_1.SampleCount, held_1.Multiplier, ws.CursorDisplayCycle, ws.CursorExactClkCycle, ws.ClkCycleBoxIsEmpty, ws.Radix, ws.WaveformColumnWidth, ws.WaveModalActive, ws.RamModalActive, ws.PortSelectComp, ws.RamComps, ws.SelectedRams, ws.RamStartLocation, ws.RamRows, ws.ShowSheetDetail, ws.SelectedSheetInstance, ws.ShowGroupDetail, ws.HoveredLabel, ws.DraggedIndex, ws.PrevSelectedWaves, ws.ScrollbarTbWidth, ws.ScrollbarTbPos, ws.ScrollbarTbOffset, ws.ScrollbarBkgWidth, ws.ScrollbarBkgRepCycs, ws.ScrollbarQueueIsEmpty, ws.SheetSearchString, ws.ComponentSearchString, ws.PortSearchString, ws.ShowOnlySelected)), onScreen);
                    default:
                        return onScreen;
                }
            }
            else {
                return matchValue_1;
            }
        }
    }
}

/**
 * The value the cursor is on, read from the waveform that is DRAWN beside it.
 * 
 * Not from the cache and not by clock cycle. The cursor is a column of the picture, so the value
 * next to it is the sample that column was drawn from - which while the picture is a window behind
 * the controls is not the cycle the numbers above it say. Reading by cycle instead answered
 * nothing at all there, and a column of "?" beside perfectly good waveforms is a worse lie than a
 * value that matches what is on screen.
 * 
 * None where nothing is drawn for this wave, or the cursor is off the end of what is: an empty row
 * has no value, and neither has a column that is not there.
 * 
 * Goes through `drawnFor` rather than reading the memo, so that it does not matter whether the
 * value column or the waveform column is built first: whichever gets there does the drawing and
 * the other finds it done. Reading the memo instead was a render behind on any render that
 * changed the picture.
 */
export function getWaveValue(ws, wave) {
    const option_1 = drawnFor(ws, wave);
    if (option_1 != null) {
        return valueAtSample(option_1, ws.CursorDisplayCycle - ws.StartCycle);
    }
    else {
        return undefined;
    }
}

